/**
 * The waiting update, held outside React so the worker can announce one before
 * anything has mounted.
 *
 * `registerServiceWorker` runs at module scope in `main.tsx`, with
 * `immediate: true`, so an update can be announced during the first paint. A
 * component cannot receive that through props, and moving registration into an
 * effect would change when the offline flag is set, which an e2e test reads.
 *
 * So the worker writes here and `UpdatePrompt` reads, through
 * `useSyncExternalStore`, the same shape `dialog-store.ts` uses.
 *
 * ## Why this exists at all
 *
 * It did not, and that was the defect. `main.tsx` registered the worker with an
 * `onOfflineReady` handler and no `onUpdateAvailable`, and
 * `registerServiceWorker` calls `handlers.onUpdateAvailable?.(...)`, so
 * `applyUpdate` was reachable from nothing. `registerType` is `prompt` and
 * `skipWaiting` is unset, so every new version installed, entered `waiting`,
 * and stayed there.
 *
 * The effect was that no deploy could reach a browser that had already visited.
 * It is invisible from the server, because a fresh browser gets the new bundle
 * and a curl looks perfect. Only a returning visitor sees it, and a returning
 * visitor is the only kind this product has. The owner hit it directly: the app
 * shell deployed, and he opened the app and saw the version from a week before.
 */

/** What the prompt needs, or null when no update is waiting. */
export interface WaitingUpdate {
  /**
   * Applies the update and reloads. Calling it discards anything unsaved in the
   * page, which is the whole reason it is offered rather than done.
   */
  readonly apply: () => void | Promise<void>;
}

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * What the prompt renders from: the waiting update, and the reason the app must
 * not reload right now.
 *
 * ONE snapshot covering both, and that is not tidiness. `useSyncExternalStore`
 * compares snapshots with `Object.is` and re-renders only when the identity
 * changes. An earlier version returned the waiting update alone and read the
 * hold separately during render, so taking a hold notified the listeners,
 * produced the identical object, and React correctly decided nothing had
 * happened: the Update button stayed on screen through a hold that was supposed
 * to remove it. Caught by the test for the hold, which is the only reason it is
 * not in this branch.
 */
export interface UpdateSnapshot {
  readonly waiting: WaitingUpdate | null;
  /** Why updates are refused, as copy to show a person, or null. */
  readonly holdReason: string | null;
}

const EMPTY: UpdateSnapshot = { waiting: null, holdReason: null };

/*
 * Rebuilt only when something actually changes, and returned by reference in
 * between, which is what `getSnapshot` requires.
 */
let snapshot: UpdateSnapshot = EMPTY;

/*
 * Reasons the app must not reload right now.
 *
 * `registerServiceWorker` states the rule this implements: an update must never
 * be applied to a running scorebook, because that can happen in the third
 * quarter to a volunteer holding an unsynced event queue and the cost is a
 * game's data in front of parents. Nothing holds today, because no scorebook
 * exists. The seam exists so the scorebook does not have to REMEMBER: it takes
 * a hold while a game is live, releases it when the game is final, and the
 * prompt refuses in between without knowing what a game is.
 *
 * Keyed rather than counted, because two things can hold at once, a live game
 * and an in-flight write, and releasing one must not release the other.
 */
const holds = new Map<symbol, string>();

function firstHold(): string | null {
  for (const reason of holds.values()) return reason;
  return null;
}

function publish(waiting: WaitingUpdate | null): void {
  snapshot = { waiting, holdReason: firstHold() };
  for (const listener of listeners) listener();
}

/** Called by the service worker registration when a new version is waiting. */
/*
 * `apply` may return its promise, and callers that can observe a rejection
 * should. `WaitingUpdate.apply` was widened for the reason recorded there: a
 * fire-and-forget wrapper discards the only handle on whether the reload is
 * actually coming.
 */
export function setWaitingUpdate(apply: () => void | Promise<void>): void {
  publish({ apply });
}

/**
 * Dismiss without applying. The update stays installed and is offered again on
 * the next load, which is what "not now" has to mean: a volunteer mid-game says
 * not now, closes the app after the final buzzer, and gets the new version then
 * without having to know anything happened.
 */
export function dismissWaitingUpdate(): void {
  publish(null);
}

/**
 * Refuse updates until the returned function is called. Give a reason a person
 * can read, because it is shown to them where the usual copy would be.
 */
export function holdUpdates(reason: string): () => void {
  const token = Symbol(reason);
  holds.set(token, reason);
  publish(snapshot.waiting);
  return () => {
    holds.delete(token);
    publish(snapshot.waiting);
  };
}

export function subscribeToUpdates(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUpdateSnapshot(): UpdateSnapshot {
  return snapshot;
}

/**
 * The server snapshot, always the same empty object.
 *
 * There is no service worker during a server render, so nothing is ever waiting
 * and nothing ever holds. Returning the shared `EMPTY` keeps it referentially
 * stable, which `useSyncExternalStore` requires.
 */
export function getServerUpdateSnapshot(): UpdateSnapshot {
  return EMPTY;
}

/** The waiting update, for tests and for callers that do not render. */
export function getWaitingUpdate(): WaitingUpdate | null {
  return snapshot.waiting;
}

/** Test seam. Nothing in the product calls this. */
export function resetWaitingUpdateForTests(): void {
  holds.clear();
  snapshot = EMPTY;
  listeners.clear();
}
